import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ButtonWrapper } from "styles/ModalCommonStyled";
import BigButtonComponent from "components/atoms/buttons/BigButton";

import Input from "components/atoms/input/Input";
import { RegisterKeyModalStyled } from "pages/subscription/SubscriptionStyled";
import PrimaryButton from "components/atoms/buttons/PrimaryButton";
import { useMemo, useRef, useState } from "react";
import { FaInfoCircle, FaMinusCircle } from "react-icons/fa";
import UploadInput from "components/atoms/input/upload/UploadInput";

import * as XLSX from "xlsx";
import { ButtonLabel } from "components/atoms/buttons/ButtonStyled";
import useStatusMutation from "hook/useStatusMutation";
import { postRegisterKey } from "api/subscriptionAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import useApiError from "hook/useApiError";
import { Text, ValidateWord } from "components/atoms/text/Text";
import { isValidKeyFormat } from "utils/SubscriptionUtil";
import { COLORS } from "styles/colors";
import { WithTooltip } from "components/blocks/WithTooltip";
import { checkEmpty, toTrim } from "utils/functions";

type Props = {
  onModal: () => void;
  refetch: () => void;
};

export default function RegisterKeyModal(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { handleErrorNotify, intlString, notifySuccess, notifyError, lang } =
    useApiError();

  const inputRef = useRef<HTMLInputElement>(null);

  const [keyInputs, setKeyInputs] = useState<string[]>([""]);
  const [validateMsg, setValidateMsg] = useState<string>("");

  const handleAddInput = () => {
    setKeyInputs((prev) => ["", ...prev]); // 빈 문자열 추가
  };
  const handleChange = (index: number, value: string) => {
    setKeyInputs((prev) => {
      const newInputs = [...prev];
      newInputs[index] = toTrim(value);
      return newInputs;
    });
  };

  const handleRemoveInput = (index: number) => {
    setKeyInputs((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target?.result;
      if (!data) return;

      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      }) as string[][];

      // 1열 데이터만 추출 (각 행의 첫 번째 셀)
      const columnData = [
        ...rawData
          .map((row) => toTrim(row[0]))
          .filter((v) => isValidKeyFormat(v) && !checkEmpty(v)),
      ];

      if (columnData.length === 0) {
        notifyError(lang.label.subscription.register.file.empty);
        return;
      }
      setKeyInputs(columnData);
      setValidateMsg("");
    };

    reader.readAsArrayBuffer(file);
  };

  const triggerFileInput = () => {
    inputRef.current?.click();
  };

  const mutationRegisterKey = useStatusMutation(postRegisterKey, {
    onSuccess: (res: any) => {
      notifySuccess(lang.label.subscription.register.success);
      props.onModal();
      props.refetch();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.register.fail);
    },
  });

  const onRegister = () => {
    const payload = keyInputs.filter((input) => !checkEmpty(input));
    if (isEmptyKeyInputs) {
      setValidateMsg(intlString(lang.label.subscription.register.empty));
      return;
    }
    setValidateMsg("");
    mutationRegisterKey.mutate({
      accountId: selectedAccount.accountId,
      payload,
    });
  };

  const isEmptyKeyInputs = useMemo(() => {
    return keyInputs.filter((input) => !checkEmpty(input)).length === 0;
  }, [keyInputs]);

  return (
    <BigPopupTemplate title="Register Subscription Key" onModal={props.onModal}>
      <RegisterKeyModalStyled.Title>
        <p>Please enter the subscription keys you have received.</p>
        <p className="subtitle">
          Subscription keys cannot be unregistered once added.
        </p>
      </RegisterKeyModalStyled.Title>

      <RegisterKeyModalStyled.Inner>
        {validateMsg && (
          <ValidateWord data-testid="subscription-modal-register-key-validate-msg">
            {validateMsg}
          </ValidateWord>
        )}
        <div className="input-inner">
          {keyInputs.map((value, index) => {
            return (
              <RegisterKeyModalStyled.InputWrapper key={index}>
                <Input
                  placeholder="Enter subscription key."
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  data-testid="subscription-modal-register-key-input-change"
                />

                {keyInputs.length > 1 && index !== 0 && (
                  <button
                    className="delete-button"
                    onClick={() => handleRemoveInput(index)}
                    data-testid="subscription-modal-register-key-button-remove-input"
                  >
                    <FaMinusCircle />
                  </button>
                )}
              </RegisterKeyModalStyled.InputWrapper>
            );
          })}
        </div>

        <RegisterKeyModalStyled.AddButtonWrapper>
          <Text color={COLORS.BUTTONS1}>
            Click to enter more subscription keys.
          </Text>
          <PrimaryButton
            label="Add"
            add
            onClickBtn={handleAddInput}
            data-testid="subscription-modal-register-key-primary-button-add-input"
          />
        </RegisterKeyModalStyled.AddButtonWrapper>
      </RegisterKeyModalStyled.Inner>

      <ButtonWrapper>
        <RegisterKeyModalStyled.UploadButton
          buttonType="cancel"
          onClick={triggerFileInput}
          data-testid="subscription-modal-register-key-upload-button-trigger-file-input"
        >
          <ButtonLabel>
            <WithTooltip tooltip="Upload an .xlsx file to register subscription keys in bulk.">
              <FaInfoCircle />
            </WithTooltip>
            Upload
          </ButtonLabel>
          <UploadInput
            type="file"
            id="files"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            ref={inputRef}
            data-testid="subscription-modal-register-key-files-file-upload"
          />
        </RegisterKeyModalStyled.UploadButton>
        <BigButtonComponent
          label={"Cancel"}
          buttonType="cancel"
          onClickBtn={props.onModal}
          data-testid="subscription-modal-register-key-button-component-modal"
        />

        <BigButtonComponent
          label="Register"
          onClickBtn={onRegister}
          disabled={isEmptyKeyInputs}
          data-testid="subscription-modal-register-key-button-component-register"
        />
      </ButtonWrapper>
    </BigPopupTemplate>
  );
}
